import React, { useEffect, useState, useContext, useRef } from "react";
import { Container } from "react-bootstrap";
import Chart from "chart.js/auto";
import APIServicenew from "../utils/APIGeneralService";
import { UserContext } from "../context/UserContextProvider";
import { useNavigate } from "react-router-dom";
import ChartDataLabels from "chartjs-plugin-datalabels";

const Dashboard = () => {
  const APIServices = new APIServicenew(process.env.REACT_APP_OTHER_API_URL);
  const { accessToken, logout } = useContext(UserContext);
  const navigate = useNavigate();

  const headers = {
    Authorization: `Bearer ${accessToken}`,
  };

  // ================= State =================
  const [summary, setSummary] = useState({});
  const [donutData, setDonutData] = useState(null);
  const [timelineData, setTimelineData] = useState([]);
  const [barData, setBarData] = useState(null);
  const [barVisibility, setBarVisibility] = useState({
    success: true,
    failed: true,
    pending: true
  });

  // ================= Chart Refs =================
  const donutChartRef = useRef(null);
  const lineChartRef = useRef(null);
  const barChartRef = useRef(null);

  // ================= Fetch Dashboard =================
  useEffect(() => {
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    try {
      const response = await APIServices.get(
        "test-execution/test-runs/dashboard",
        headers
      );

      if (response?.status === 200) {
        const data = response.data;

        setSummary(data.summary || {});
        setDonutData(data.donut_chart || {});
        setTimelineData(data.timeline_chart || []);
        setBarData(data.distribution_bar_chart || {});
      } else if (response?.status === 401) {
        logout();
        navigate("/");
      }
    } catch (error) {
      console.error("Dashboard error:", error);
    }
  };

  // ================= Chart Lifecycle =================
  useEffect(() => {
    destroyCharts();

    if (donutData) createDonutChart();
    if (timelineData.length) createLineChart();
    if (barData) createBarChart();

    return () => destroyCharts();
  }, [donutData, timelineData, barData, barVisibility]);

  const destroyCharts = () => {
    donutChartRef.current?.destroy();
    lineChartRef.current?.destroy();
    barChartRef.current?.destroy();
  };

  // ================= Charts =================

  const createDonutChart = () => {
    const ctx = document.getElementById("donut");
    if (!ctx) return;

    const total = donutData.mtf + donutData.mtc;

    donutChartRef.current = new Chart(ctx, {
      type: "doughnut",
      data: {
        labels: ["MTF", "MTC"],
        datasets: [
          {
            data: [donutData.mtf, donutData.mtc],
            backgroundColor: ["#00aaf6", "#77ccf2"],
            borderWidth: 0,
          },
        ],
      },
      options: {
        plugins: {
          legend: {
            position: "top",
            labels: {
              generateLabels: (chart) => {
                const data = chart.data.datasets[0].data;
                const labels = chart.data.labels;
                return labels.map((label, i) => ({
                  text: `${label} - ${Math.round((data[i] / total) * 100)}%`,
                  fillStyle: chart.data.datasets[0].backgroundColor[i],
                  strokeStyle: chart.data.datasets[0].backgroundColor[i],
                  index: i,
                }));
              },
            },
          },
          tooltip: {
            callbacks: {
              label: (context) => {
                const value = context.raw;
                const percent = ((value / total) * 100).toFixed(0);
                return `${context.label} - ${percent}%`;
              },
            },
          },
        },
      },
    });
  };

  const createLineChart = () => {
    const ctx = document.getElementById("line");
    if (!ctx) return;

    lineChartRef.current = new Chart(ctx, {
      type: "line",
      data: {
        labels: timelineData.map((item) => item.month),
        datasets: [
          {
            label: "Success Rate (%)",
            data: timelineData.map((item) => item.success_rate),
            borderColor: "#00aaf6",
            backgroundColor: "#77ccf2",
             pointBackgroundColor:"#f97316",
            fill: true,
            tension: 0.4,
          },
        ],
      },
      options: {
        scales: {
          y: {
            min: 0,
            max: 100,
          },
        },
      },
    });
  };

  const createBarChart = () => {
    const ctx = document.getElementById("callOutcomeChart");
    if (!ctx) return;

    // Filter data based on visibility state
    const filteredLabels = [];
    const filteredData = [];
    const filteredBackgroundColors = [];

    if (barVisibility.success) {
      filteredLabels.push("Success");
      filteredData.push(barData.success_percent);
      filteredBackgroundColors.push("#00aaf6"); // single color
    }

    if (barVisibility.failed) {
      filteredLabels.push("Failed");
      filteredData.push(barData.failed_percent);
      filteredBackgroundColors.push("#00aaf6"); // single color
    }

    if (barVisibility.pending) {
      filteredLabels.push("Pending");
      filteredData.push(barData.pending_percent);
      filteredBackgroundColors.push("#00aaf6"); // single color
    }

    barChartRef.current = new Chart(ctx, {
      type: "bar",
      data: {
        labels: filteredLabels,
        datasets: [
          {
            data: filteredData,
            backgroundColor: filteredBackgroundColors,
            borderRadius: 8,
          },
        ],
      },
      options: {
        indexAxis: "y",
        plugins: {
          legend: { display: false },
          tooltip: {
            callbacks: {
              label: (context) => `${context.label} - ${context.raw}%`,
            },
          },
          datalabels: {
            display: true,
            anchor: "end",
            align: "end",
            formatter: (value) => `${value}%`,
            color: "#000",
            font: {
              weight: "bold", // bold percentage at end of bar
            },
          },
        },
        scales: {
          x: {
            min: 0,
            max: 100,
            ticks: {
              callback: (value) => `${value}%`,
              font: { weight: "bold" }, // bold X-axis numbers
            },
          },
          y: {
            ticks: {
              font: { weight: "bold" }, // bold labels: Success, Failed, Pending
            },
          },
        },
      },
      plugins: [ChartDataLabels],
    });
  };


  // Toggle bar visibility
  const toggleBarVisibility = (barType) => {
    setBarVisibility((prev) => ({
      ...prev,
      [barType]: !prev[barType],
    }));
  };

  // ================= Counters =================
  const stats = [
    { title: "Total Users", value: summary.total_users },
    { title: "Total Test-Suites", value: summary.total_folders },
    { title: "Total Scripts", value: summary.total_test_cases },
    { title: "Automation Failures", value: summary.failed_test_cases },
    { title: "Passing Rate", value: summary.passed_test_cases },
  ];

  return (
    <Container fluid>
      {/* ================= Counters ================= */}
      <div className="stats_section">
        <div className="row g-3">


          {/* Total Test-Suites */}
          <div className="col-md">
            <div className="stat-card white_box">
              <div className="stat-icon bg-purple">
                <svg width="17" height="18" viewBox="0 0 17 18" fill="currentColor">
                  <path d="M8.25 0L9.3998 6.11501C9.59244 7.13949 10.4005 7.93691 11.4275 8.1159L16.5 9L11.4275 9.8841C10.4005 10.0631 9.59244 10.8605 9.3998 11.885L8.25 18L7.1002 11.885C6.90757 10.8605 6.09946 10.0631 5.07251 9.8841L0 9L5.07251 8.1159C6.09947 7.93691 6.90757 7.13949 7.1002 6.11501L8.25 0Z" />
                </svg>
              </div>
              <div className="stat_content">
                <p className="font_14 font-500 dgrey mb-2">Total Test-Suites</p>
                <h2 className="font_20 font_600 clr_blue">
                  {summary.total_folders || 0}
                </h2>
              </div>
            </div>
          </div>

          {/* Total Scripts */}
          <div className="col-md">
            <div className="stat-card white_box">
              <div className="stat-icon bg-pink">
                <svg width="17" height="18" viewBox="0 0 17 18" fill="currentColor">
                  <path d="M8.25 0L9.3998 6.11501C9.59244 7.13949 10.4005 7.93691 11.4275 8.1159L16.5 9L11.4275 9.8841C10.4005 10.0631 9.59244 10.8605 9.3998 11.885L8.25 18L7.1002 11.885C6.90757 10.8605 6.09946 10.0631 5.07251 9.8841L0 9L5.07251 8.1159C6.09947 7.93691 6.90757 7.13949 7.1002 6.11501L8.25 0Z" />
                </svg>
              </div>
              <div className="stat_content">
                <p className="font_14 font-500 dgrey mb-2">Total Scripts</p>
                <h2 className="font_20 font_600 clr_blue">
                  {summary.total_test_cases || 0}
                </h2>
              </div>
            </div>
          </div>

          {/* Automation Failures */}
          <div className="col-md">
            <div className="stat-card white_box">
              <div className="stat-icon bg-orange">
                <svg width="17" height="18" viewBox="0 0 17 18" fill="currentColor">
                  <path d="M8.25 0L9.3998 6.11501C9.59244 7.13949 10.4005 7.93691 11.4275 8.1159L16.5 9L11.4275 9.8841C10.4005 10.0631 9.59244 10.8605 9.3998 11.885L8.25 18L7.1002 11.885C6.90757 10.8605 6.09946 10.0631 5.07251 9.8841L0 9L5.07251 8.1159C6.09947 7.93691 6.90757 7.13949 7.1002 6.11501L8.25 0Z" />
                </svg>
              </div>
              <div className="stat_content">
                <p className="font_14 font-500 dgrey mb-2">Automation Failures</p>
                <h2 className="font_20 font_600 clr_blue">
                  {summary.failed_test_cases || 0}
                </h2>
              </div>
            </div>
          </div>

          {/* Passing Rate */}
          <div className="col-md">
            <div className="stat-card white_box">
              <div className="stat-icon bg-green">
                <svg width="17" height="18" viewBox="0 0 17 18" fill="currentColor">
                  <path d="M8.25 0L9.3998 6.11501C9.59244 7.13949 10.4005 7.93691 11.4275 8.1159L16.5 9L11.4275 9.8841C10.4005 10.0631 9.59244 10.8605 9.3998 11.885L8.25 18L7.1002 11.885C6.90757 10.8605 6.09946 10.0631 5.07251 9.8841L0 9L5.07251 8.1159C6.09947 7.93691 6.90757 7.13949 7.1002 6.11501L8.25 0Z" />
                </svg>
              </div>
              <div className="stat_content">
                <p className="font_14 font-500 dgrey mb-2">Stability Rate(Pass Testcases)</p>
                <h2 className="font_20 font_600 clr_blue">
                  {summary.passed_test_cases || 0}
                </h2>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ================= Charts ================= */}
      <div className="dashboard_chart_sec mt-4">
        <div className="row g-4">
          <div className="col-lg-4">
            <div className="white_box h-100">
              <h2 className="font_16 font-500 clr_blue mb-3">
                Total Testsuites v/s Total Test Case Run
              </h2>
              <canvas id="donut" />
            </div>
          </div>

          <div className="col-lg-8">
            <div className="white_box h-100">
              <h2 className="font_16 font-500 clr_blue mb-3">
                Timeline: Test Success Rate
              </h2>
              <canvas id="line" />
            </div>
          </div>
        </div>
      </div>

      {/* ================= Bar Chart with Clickable Color Codes ================= */}
      <div className="dashboard_bar_sec mt-4">
        <div className="white_box">
          <h2 className="font_16 font-500 clr_blue mb-3">
            Test Run Distribution

            {/* color codes */}
            {/* <div className="d-inline-flex align-items-center gap-3 ms-3">
              <button
                className="btn p-0 border-0 bg-transparent font_14 font-500 d-flex align-items-center gap-1"
                onClick={() => toggleBarVisibility("success")}
                style={{
                  opacity: barVisibility.success ? 1 : 0.5,
                  cursor: "pointer",
                }}
              >
                <span
                  className="d-inline-block"
                  style={{
                    width: "36px",
                    height: "12px",
                    backgroundColor: "#22c55e",
                    borderRadius: "2px",
                  }}
                ></span>
                Success - {barData?.success_percent || 0}%
              </button>
              <button
                className="btn p-0 border-0 bg-transparent font_14 font-500 d-flex align-items-center gap-1"
                onClick={() => toggleBarVisibility("failed")}
                style={{
                  opacity: barVisibility.failed ? 1 : 0.5,
                  cursor: "pointer",
                }}
              >
                <span
                  className="d-inline-block"
                  style={{
                    width: "36px",
                    height: "12px",
                    backgroundColor: "#ef4444",
                    borderRadius: "2px",
                  }}
                ></span>
                Failed - {barData?.failed_percent || 0}%
              </button>
              <button
                className="btn p-0 border-0 bg-transparent font_14 font-500 d-flex align-items-center gap-1"
                onClick={() => toggleBarVisibility("pending")}
                style={{
                  opacity: barVisibility.pending ? 1 : 0.5,
                  cursor: "pointer",
                }}
              >
                <span
                  className="d-inline-block"
                  style={{
                    width: "36px",
                    height: "12px",
                    backgroundColor: "#f97316",
                    borderRadius: "2px",
                  }}
                ></span>
                Pending - {barData?.pending_percent || 0}%
              </button>
            </div> */}
          </h2>
          <canvas id="callOutcomeChart" />
        </div>
      </div>
    </Container>
  );
};

export default Dashboard;